import {
  PROTOCOL_VERSION,
  decodeServer,
  encode,
  type ClientMessage,
  type Intent,
  type MatchEndMsg,
  type MatchmakingMsg,
  type MatchStartMsg,
  type RoomErrorCode,
  type RoomStateMsg,
  type ServerMessage,
  type SimEvent,
  type SnapshotMsg,
  type WeaponId,
} from '@cuboom/shared';
import { loadSquad } from '../game/squad.js';

export type NetStatus = 'idle' | 'connecting' | 'online' | 'offline';

export interface NetHandlers {
  onStatus(status: NetStatus, detail?: string): void;
  onWelcome(playerId: string): void;
  onRoomState(msg: RoomStateMsg): void;
  onMatchStart(msg: MatchStartMsg): void;
  onSnapshot(msg: SnapshotMsg): void;
  onEvents(events: SimEvent[]): void;
  onAck(ok: boolean, reason?: string): void;
  /**
   * Lockstep input relay: a human intent the server applied at `tick` —
   * queue it and apply it in our local sim at exactly that tick.
   */
  onIntentApplied(msg: { intent: Intent; tick: number }): void;
  /** Clock beacon: the server's current sim tick of our room (0 outside). */
  onPong(tick: number): void;
  /** Another seat's live aim (server-stamped playerId). */
  onAimState(msg: { playerId: string; x: number; y: number; weaponId: WeaponId | null }): void;
  onMatchEnd(winnerTeamId: number | null, stats: MatchEndMsg['stats']): void;
  onPlayerEvent(kind: 'joined' | 'left' | 'disconnected' | 'reconnected', name: string, graceS?: number): void;
  onMatchmaking(msg: MatchmakingMsg): void;
  onError(code: RoomErrorCode, msg: string): void;
}

const TOKEN_KEY = 'cuboom.playerId';
const NAME_KEY = 'cuboom.name';
/** Exponential backoff between reconnect attempts, capped at 8 s. */
const RETRY_MS = [500, 1000, 2000, 4000, 8000];

/**
 * Thin WebSocket wrapper: connect (with auto-reconnect + resync), send
 * intents, and dispatch server messages to the handlers. No simulation and
 * no game knowledge lives here beyond the message shapes.
 */
export class Connection {
  status: NetStatus = 'idle';
  selfId = '';
  /** Set once the server confirms our identity; reused for reconnects. */
  private token = '';
  private ws: WebSocket | null = null;
  private retry = 0;
  private retryTimer: number | null = null;
  private closedByUs = false;
  private roomCode: string | null = null;
  private latencyMs = 0;
  private pingTimer: number | null = null;
  /** Room to rejoin as ourselves after a dropped socket. */
  private pendingRejoin: string | null = null;
  /** True once the server has answered our `hello`; earlier sends are refused. */
  private welcomed = false;
  /** A search asked for before the handshake finished, held until `welcome`. */
  private pendingFind = false;

  constructor(
    private readonly url: string,
    private readonly h: NetHandlers,
  ) {}

  get isOnline(): boolean {
    return this.status === 'online' && this.ws?.readyState === WebSocket.OPEN;
  }

  get ping(): number {
    return this.latencyMs;
  }

  get code(): string | null {
    return this.roomCode;
  }

  static storedName(): string {
    try {
      return localStorage.getItem(NAME_KEY) ?? '';
    } catch {
      return '';
    }
  }

  static storeName(name: string): void {
    try {
      localStorage.setItem(NAME_KEY, name);
    } catch {
      /* ignore */
    }
  }

  // --- lifecycle -----------------------------------------------------------

  connect(name: string): void {
    this.closedByUs = false;
    this.open(name);
  }

  private open(name: string): void {
    this.clearTimers();
    this.welcomed = false;
    this.setStatus('connecting');
    try {
      this.token = localStorage.getItem(TOKEN_KEY) ?? '';
    } catch {
      this.token = '';
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      this.setStatus('offline', String(err));
      this.scheduleRetry();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      this.setStatus('online');
      this.sendRaw({ t: 'hello', d: { protocolVersion: PROTOCOL_VERSION, name } });
    };

    ws.onmessage = (ev) => {
      const msg = decodeServer(String(ev.data));
      if (msg) this.dispatch(msg);
    };

    ws.onclose = () => {
      this.ws = null;
      this.clearTimers();
      if (this.closedByUs) {
        this.setStatus('idle');
        return;
      }
      this.setStatus('offline', 'connection lost');
      this.scheduleRetry();
    };

    ws.onerror = () => {
      // onclose always follows; nothing to do but let it retry.
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    this.clearTimers();
    this.ws?.close();
    this.ws = null;
    this.roomCode = null;
    this.pendingFind = false;
    this.setStatus('idle');
  }

  private scheduleRetry(): void {
    const wait = RETRY_MS[Math.min(this.retry, RETRY_MS.length - 1)];
    this.retry++;
    this.retryTimer = window.setTimeout(() => this.reconnect(), wait);
  }

  /** Reconnect and, if we were in a room, reattach as the same player. */
  private reconnect(): void {
    const name = Connection.storedName() || 'player';
    const code = this.roomCode;
    this.open(name);
    // The join is re-sent after welcome (dispatch handles it).
    if (code) this.pendingRejoin = code;
  }

  private clearTimers(): void {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // --- dispatch ------------------------------------------------------------

  private dispatch(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome': {
        this.selfId = msg.d.playerId;
        this.welcomed = true;
        try {
          localStorage.setItem(TOKEN_KEY, msg.d.playerId);
        } catch {
          /* ignore */
        }
        this.h.onWelcome(msg.d.playerId);
        this.startPing();
        if (this.pendingRejoin) {
          const code = this.pendingRejoin;
          this.pendingRejoin = null;
          this.send({
            t: 'join_room',
            d: { code, name: Connection.storedName() || 'player', token: this.selfId, squad: loadSquad() },
          });
        } else if (this.pendingFind) {
          this.pendingFind = false;
          this.send({
            t: 'find_match',
            d: { name: Connection.storedName() || 'player', token: this.selfId, squad: loadSquad() },
          });
        }
        return;
      }
      case 'room_state':
        // A room arrival means any pending rejoin has been superseded.
        this.pendingRejoin = null;
        this.roomCode = msg.d.code;
        this.h.onRoomState(msg.d);
        return;
      case 'room_error':
        this.h.onError(msg.d.code, msg.d.msg);
        return;
      case 'match_start':
        this.pendingRejoin = null; // we are in the match now
        this.clearTimers();
        this.startPing();
        this.h.onMatchStart(msg.d);
        return;
      case 'snapshot':
        this.h.onSnapshot(msg.d);
        return;
      case 'sim_event':
        this.h.onEvents(msg.d.events);
        return;
      case 'intent_ack':
        this.h.onAck(msg.d.ok, msg.d.reason);
        return;
      case 'intent_applied':
        // Lockstep: apply this input at exactly this tick in our own sim.
        this.h.onIntentApplied(msg.d);
        return;
      case 'aim_state':
        this.h.onAimState(msg.d);
        return;
      case 'match_end':
        this.h.onMatchEnd(msg.d.winnerTeamId, msg.d.stats);
        return;
      case 'player_event':
        this.h.onPlayerEvent(msg.d.kind, msg.d.name, msg.d.graceS);
        return;
      case 'matchmaking':
        // Being in the queue supersedes any old room: the server has already
        // detached us from it, so forget the code a reconnect would rejoin.
        this.pendingRejoin = null;
        this.roomCode = null;
        this.h.onMatchmaking(msg.d);
        return;
      case 'pong':
        this.latencyMs = Math.max(0, Date.now() - msg.d.t);
        this.h.onPong(msg.d.tick);
        return;
      default:
        return;
    }
  }

  private startPing(): void {
    if (this.pingTimer !== null) return;
    this.pingTimer = window.setInterval(() => {
      if (this.isOnline) this.send({ t: 'ping', d: { t: Date.now() } });
    }, 3000);
  }

  // --- send ----------------------------------------------------------------

  private sendRaw(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encode(msg));
  }

  send(msg: ClientMessage): boolean {
    if (!this.isOnline) return false;
    this.sendRaw(msg);
    return true;
  }

  createRoom(name: string, config: RoomStateMsg['config'], code?: string): boolean {
    return this.send({
      t: 'create_room',
      d: { name, config, squad: loadSquad(), ...(code ? { code } : {}) },
    });
  }

  joinRoom(code: string, name: string): boolean {
    return this.send({
      t: 'join_room',
      d: { code, name, token: this.selfId || undefined, squad: loadSquad() },
    });
  }

  /** Instant match: queue for strangers, no invite code involved. */
  findMatch(name: string): boolean {
    // Before `welcome` the server would reject this with BAD_INPUT, so hold it
    // and send it as soon as the handshake lands.
    if (!this.welcomed) {
      this.pendingFind = true;
      return this.isOnline;
    }
    return this.send({
      t: 'find_match',
      d: { name, token: this.selfId || undefined, squad: loadSquad() },
    });
  }

  cancelMatch(): void {
    this.send({ t: 'cancel_match' });
  }

  leaveRoom(): void {
    this.send({ t: 'leave_room' });
    this.roomCode = null;
  }

  setReady(ready: boolean): void {
    this.send({ t: 'set_ready', d: { ready } });
  }

  setConfig(config: Partial<RoomStateMsg['config']>): void {
    this.send({ t: 'set_config', d: { config } });
  }

  startNow(): void {
    // "Start" is just ready=true from every seat; the server starts the match
    // once all connected seats are ready.
    this.setReady(true);
  }

  submitIntent(intent: Intent): void {
    this.send({ t: 'submit_intent', d: { intent } });
  }

  requestState(): void {
    this.send({ t: 'request_state' });
  }

  setName(name: string): void {
    Connection.storeName(name);
  }

  private setStatus(s: NetStatus, detail?: string): void {
    this.status = s;
    this.h.onStatus(s, detail);
  }
}
